import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Card, CardContent, Grid, Chip,
  TextField, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Alert, CircularProgress, Divider, Paper, Snackbar, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Send as SendIcon,
  Person as PersonIcon, LocalShipping as TruckIcon,
  LocationOn as LocationIcon, ArrowBack as BackIcon,
  CheckCircle as CheckIcon, PersonAdd as PersonAddIcon,
  Visibility as ViewIcon, AccessTime as TimeIcon,
  CalendarToday as CalendarIcon, Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import DireccionInput from '../../components/forms/DireccionInput';
import { useAuth } from '../../context/AuthContext';
import { useEmpresa } from '../../context/EmpresaContext';
import axios from 'axios';
import config from '../../config';

const STATUS_COLORS = {
  1: '#FB8C00', 2: '#1E88E5', 3: '#7B1FA2', 4: '#43A047',
  5: '#2E7D32', 6: '#FFA000', 7: '#C62828', 8: '#4CAF50',
};

export default function DeliveryRuta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const headers = { Authorization: `Bearer ${user?.token}` };
  const moneda = simboloMoneda || 'S/';

  const [orden, setOrden] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [resumen, setResumen] = useState({});
  const [trackerAsignado, setTrackerAsignado] = useState(null);
  const [estadoPadre, setEstadoPadre] = useState(1);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  // Dialog nueva entrega
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entrega, setEntrega] = useState({
    id_cliente: null, id_servicio: null, precio: '', cantidad: 1,
    detalle_entrega: '', direccion: '', latitud: null, longitud: null,
    referencia: '', comision_monto: 0, tipo_comision: 'FLAT', comision_porcentaje: 0,
  });

  // Dialog asignar motorizado
  const [asignarOpen, setAsignarOpen] = useState(false);
  const [motorizado, setMotorizado] = useState({ id_trabajador: null, fecha_servicio: '', hora_servicio: '' });
  const [comisionesEdit, setComisionesEdit] = useState([]);
  const [asignando, setAsignando] = useState(false);

  // Dialog resumen
  const [resumenOpen, setResumenOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ordenRes, entregasRes, clientesRes, serviciosRes, trabajadoresRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/ordenes/${id}`, { headers }),
        axios.get(`${config.API_BASE_URL}/api/v1/delivery-rutas/${id}/entregas`, { headers }),
        axios.get(`${config.API_BASE_URL}/api/clientes`, { headers }),
        axios.get(`${config.API_BASE_URL}/api/servicios`, { headers }),
        axios.get(`${config.API_BASE_URL}/api/trabajadores`, { headers }),
      ]);
      setOrden(ordenRes.data);
      setEntregas(entregasRes.data?.data || []);
      setResumen(entregasRes.data?.resumen || {});
      setTrackerAsignado(entregasRes.data?.tracker_asignado || null);
      setEstadoPadre(entregasRes.data?.estado_padre || 1);
      setClientes(clientesRes.data || []);
      setServicios(serviciosRes.data || []);
      setTrabajadores(trabajadoresRes.data || []);
    } catch (e) {
      console.error('Error cargando ruta:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAgregarEntrega = async () => {
    if (!entrega.id_cliente) {
      setSnackbar({ open: true, msg: 'Selecciona un cliente', severity: 'error' });
      return;
    }
    if (!entrega.id_servicio) {
      setSnackbar({ open: true, msg: 'Selecciona un servicio', severity: 'error' });
      return;
    }
    if (!entrega.precio || parseFloat(entrega.precio) <= 0) {
      setSnackbar({ open: true, msg: 'El precio es obligatorio', severity: 'error' });
      return;
    }
    if (!entrega.direccion) {
      setSnackbar({ open: true, msg: 'La dirección de entrega es obligatoria', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      // Calcular comisión según tipo
      const precioNum = parseFloat(entrega.precio) || 0;
      const comisionCalculada = entrega.tipo_comision === 'PORCENTAJE'
        ? Math.round(precioNum * (parseFloat(entrega.comision_porcentaje) || 0) / 100 * 100) / 100
        : parseFloat(entrega.comision_monto) || 0;

      const payload = {
        ...entrega,
        precio: precioNum,
        comision_monto: comisionCalculada,
      };
      await axios.post(`${config.API_BASE_URL}/api/v1/delivery-rutas/${id}/entregas`, payload, { headers });
      setDialogOpen(false);
      setEntrega({ id_cliente: null, id_servicio: null, precio: '', cantidad: 1, detalle_entrega: '', direccion: '', latitud: null, longitud: null, referencia: '', comision_monto: 0, tipo_comision: 'FLAT', comision_porcentaje: 0 });
      fetchData();
      setSnackbar({ open: true, msg: 'Entrega agregada', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.error || 'Error', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarEntrega = async (idHija) => {
    if (!window.confirm('¿Eliminar esta entrega?')) return;
    try {
      await axios.delete(`${config.API_BASE_URL}/api/v1/delivery-rutas/${id}/entregas/${idHija}`, { headers });
      fetchData();
      setSnackbar({ open: true, msg: 'Entrega eliminada', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, msg: 'Error al eliminar', severity: 'error' });
    }
  };

  const handleOpenAsignar = () => {
    // Pre-cargar comisiones desde las entregas
    setComisionesEdit(entregas.map(e => ({
      id_orden_servicio: e.id_orden_servicio,
      nombre: `${e.nombre_cliente || ''} ${e.apellido_cliente || ''}`.trim(),
      servicio: e.nombre_servicio || '',
      precio: e.precio_total || 0,
      comision_flat: e.comision_entrega || 0,
    })));
    // Pre-cargar fecha y hora de la orden (usar zona horaria local, NO UTC)
    const fechaOrden = orden?.fecha_orden ? new Date(orden.fecha_orden) : new Date();
    const yyyy = fechaOrden.getFullYear();
    const mm = String(fechaOrden.getMonth() + 1).padStart(2, '0');
    const dd = String(fechaOrden.getDate()).padStart(2, '0');
    const fechaStr = `${yyyy}-${mm}-${dd}`;
    const horaStr = `${String(fechaOrden.getHours()).padStart(2, '0')}:${String(fechaOrden.getMinutes()).padStart(2, '0')}`;
    // Pre-cargar tracker asignado si existe
    setMotorizado({
      id_trabajador: trackerAsignado?.id_trabajador || null,
      fecha_servicio: fechaStr,
      hora_servicio: horaStr,
    });
    setAsignarOpen(true);
  };

  const handleComisionChange = (idx, value) => {
    setComisionesEdit(prev => prev.map((c, i) => i === idx ? { ...c, comision_flat: value } : c));
  };

  const comisionTotalEdit = comisionesEdit.reduce((s, c) => s + (parseFloat(c.comision_flat) || 0), 0);

  const handleAsignarMotorizado = async () => {
    if (!motorizado.id_trabajador) {
      setSnackbar({ open: true, msg: 'Selecciona un motorizado', severity: 'error' });
      return;
    }
    if (!motorizado.fecha_servicio) {
      setSnackbar({ open: true, msg: 'La fecha de inicio es obligatoria', severity: 'error' });
      return;
    }
    if (!motorizado.hora_servicio) {
      setSnackbar({ open: true, msg: 'La hora de inicio es obligatoria', severity: 'error' });
      return;
    }
    // Validar que todas las entregas tengan servicio y precio (ya guardado)
    const sinPrecio = comisionesEdit.filter(c => c.precio <= 0);
    if (sinPrecio.length > 0) {
      setSnackbar({ open: true, msg: `${sinPrecio.length} entrega(s) no tienen precio. Edítalas antes de asignar.`, severity: 'error' });
      return;
    }
    setAsignando(true);
    try {
      const payload = {
        id_trabajador: motorizado.id_trabajador,
        fecha_servicio: motorizado.fecha_servicio || null,
        hora_servicio: motorizado.hora_servicio || null,
        comisiones_individuales: comisionesEdit.map(c => ({
          id_orden_servicio: c.id_orden_servicio,
          comision_flat: parseFloat(c.comision_flat) || 0,
        })),
      };
      const res = await axios.post(`${config.API_BASE_URL}/api/v1/delivery-rutas/${id}/asignar-motorizado`, payload, { headers });
      setAsignarOpen(false);
      fetchData();
      setSnackbar({ open: true, msg: res.data.mensaje, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, msg: e.response?.data?.error || 'Error', severity: 'error' });
    } finally {
      setAsignando(false);
    }
  };

  // Tracking dialog
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingPreview, setTrackingPreview] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingSending, setTrackingSending] = useState(false);

  const handleEnviarTracking = async () => {
    setTrackingOpen(true);
    setTrackingPreview(null);
    setTrackingLoading(true);
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/v1/delivery-rutas/${id}/tracking-preview`, { headers });
      setTrackingPreview(res.data);
    } catch (e) {
      setSnackbar({ open: true, msg: 'Error al cargar preview', severity: 'error' });
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleConfirmarEnvio = async () => {
    setTrackingSending(true);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/v1/delivery-rutas/${id}/enviar-tracking`, {}, { headers });
      setTrackingOpen(false);
      setSnackbar({ open: true, msg: res.data.mensaje, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, msg: 'Error al enviar tracking', severity: 'error' });
    } finally {
      setTrackingSending(false);
    }
  };

  const handleClienteSelect = (cliente) => {
    if (!cliente) {
      setEntrega(prev => ({ ...prev, id_cliente: null }));
      return;
    }
    setEntrega(prev => ({ ...prev, id_cliente: cliente.id_cliente }));
  };

  const handleServicioSelect = (e) => {
    const srv = servicios.find(s => s.id_servicio === e.target.value);
    setEntrega(prev => ({
      ...prev,
      id_servicio: e.target.value,
      precio: srv?.precio || srv?.precio_base || prev.precio,
    }));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/home/ordenes')}><BackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1A237E">
            Ruta de Delivery #{id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {orden?.nombre_local || 'Local'} — {new Date(orden?.fecha_orden).toLocaleDateString()}
            {orden?.fecha_orden && ` ${new Date(orden.fecha_orden).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
          </Typography>
          {trackerAsignado && (
            <Chip icon={<PersonIcon />} size="small"
              label={`${trackerAsignado.nombre_trabajador} ${trackerAsignado.apellido_trabajador || ''}`}
              sx={{ mt: 0.5, bgcolor: '#10B981', color: '#fff', fontWeight: 600 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {entregas.length > 0 && (
            <>
              <Button variant="outlined" startIcon={<ViewIcon />} onClick={() => setResumenOpen(true)} size="small">
                Resumen
              </Button>
              <Button variant="outlined" startIcon={<SendIcon />} onClick={handleEnviarTracking} size="small">
                Tracking
              </Button>
              <Button variant="outlined" startIcon={<TruckIcon />} onClick={handleOpenAsignar} size="small" color="success"
                disabled={estadoPadre >= 3}>
                {trackerAsignado ? 'Cambiar tracker' : 'Asignar tracker'}
              </Button>
            </>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: '#1A237E', '&:hover': { bgcolor: '#0D47A1' } }}>
            Agregar entrega
          </Button>
        </Box>
      </Box>

      {/* Resumen */}
      {entregas.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #EEF2FF, #F0F9FF)', border: '1px solid #C7D2FE' }}>
          <Grid container spacing={2} textAlign="center">
            <Grid item xs={2.4}>
              <Typography variant="caption" color="text.secondary">Entregas</Typography>
              <Typography variant="h5" fontWeight={800} color="#1A237E">{resumen.total_entregas || 0}</Typography>
            </Grid>
            <Grid item xs={2.4}>
              <Typography variant="caption" color="text.secondary">Completadas</Typography>
              <Typography variant="h5" fontWeight={800} color="#10B981">{resumen.completadas || 0}</Typography>
            </Grid>
            <Grid item xs={2.4}>
              <Typography variant="caption" color="text.secondary">Monto Total</Typography>
              <Typography variant="h5" fontWeight={800} color="#1A237E">{moneda} {(resumen.monto_total || 0).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={2.4}>
              <Typography variant="caption" color="text.secondary">Comisión</Typography>
              <Typography variant="h5" fontWeight={800} color="#F59E0B">{moneda} {(resumen.comision_total || 0).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={2.4}>
              <Typography variant="caption" color="text.secondary">Punto de recojo</Typography>
              <Typography variant="body2" fontWeight={600} color="#1A237E">{orden?.direccion_origen || orden?.nombre_local || '-'}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Lista de entregas */}
      {entregas.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '2px dashed #E2E8F0' }}>
          <TruckIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 1 }} />
          <Typography color="text.secondary" sx={{ mb: 2 }}>No hay entregas aún</Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: '#1A237E' }}>
            Agregar primera entrega
          </Button>
        </Paper>
      ) : (
        entregas.map((e, i) => {
          const color = STATUS_COLORS[e.id_estado_servicio] || '#999';
          return (
            <Card key={e.id_orden_servicio} sx={{ mb: 1.5, borderRadius: 2, borderLeft: `4px solid ${color}`, border: '1px solid #E2E8F0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1 }}>
                    <Chip label={e.orden_secuencia || i + 1} size="small"
                      sx={{ bgcolor: color, color: '#fff', fontWeight: 700, minWidth: 28 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {e.nombre_cliente} {e.apellido_cliente}
                      </Typography>
                      {e.detalle_entrega && (
                        <Typography variant="caption" color="text.secondary">{e.detalle_entrega}</Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 13, color: '#94A3B8' }} />
                        <Typography variant="caption" color="text.secondary">
                          {e.direccion_origen || 'Sin dirección'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} color="#1A237E">
                      {moneda} {(e.precio_total || 0).toFixed(2)}
                    </Typography>
                    <Chip label={e.descripcion_estado || 'Pendiente'} size="small"
                      sx={{ bgcolor: `${color}20`, color, fontWeight: 600, fontSize: 10 }} />
                    <IconButton size="small" color="error" onClick={() => handleEliminarEntrega(e.id_orden_servicio)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Dialog: Agregar Entrega */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon sx={{ color: '#1A237E' }} />
            <Typography variant="h6" fontWeight={700}>Agregar Entrega</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Cliente */}
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Cliente *
          </Typography>
          <Autocomplete
            options={clientes}
            getOptionLabel={(opt) => `${opt.nombre_cliente} ${opt.apellido_cliente || ''} — ${opt.telefono_cliente || ''}`.trim()}
            value={clientes.find(c => c.id_cliente === entrega.id_cliente) || null}
            onChange={(_, val) => handleClienteSelect(val)}
            size="small" sx={{ mb: 2 }}
            renderInput={(params) => <TextField {...params} placeholder="Buscar cliente..." />}
          />

          {/* Servicio */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Servicio</InputLabel>
            <Select value={entrega.id_servicio || ''} onChange={handleServicioSelect} label="Servicio">
              {servicios.map(s => (
                <MenuItem key={s.id_servicio} value={s.id_servicio}>
                  {s.nombre_servicio} — {moneda} {s.precio || s.precio_base || 0}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Precio y cantidad */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label={`Precio (${moneda})`} type="number"
                value={entrega.precio} onChange={(e) => {
                  const newPrecio = e.target.value;
                  setEntrega(prev => {
                    const p = parseFloat(newPrecio) || 0;
                    // Si es porcentaje, recalcular el monto
                    if (prev.tipo_comision === 'PORCENTAJE') {
                      return { ...prev, precio: newPrecio, comision_monto: Math.round(p * (parseFloat(prev.comision_porcentaje) || 0) / 100 * 100) / 100 };
                    }
                    // Si es flat, recalcular el porcentaje
                    return { ...prev, precio: newPrecio, comision_porcentaje: p > 0 ? Math.round((parseFloat(prev.comision_monto) || 0) / p * 10000) / 100 : 0 };
                  });
                }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Cantidad" type="number"
                value={entrega.cantidad} onChange={(e) => setEntrega(prev => ({ ...prev, cantidad: parseInt(e.target.value) || 1 }))} />
            </Grid>
          </Grid>

          {/* Comisión con toggle porcentaje/flat */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={entrega.tipo_comision} label="Tipo"
                  onChange={(e) => setEntrega(prev => ({ ...prev, tipo_comision: e.target.value }))}>
                  <MenuItem value="FLAT">Monto fijo</MenuItem>
                  <MenuItem value="PORCENTAJE">Porcentaje</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="% Comisión" type="number"
                value={entrega.comision_porcentaje}
                disabled={entrega.tipo_comision === 'FLAT'}
                onChange={(e) => {
                  const pct = e.target.value;
                  setEntrega(prev => ({
                    ...prev,
                    comision_porcentaje: pct,
                    comision_monto: Math.round((parseFloat(prev.precio) || 0) * (parseFloat(pct) || 0) / 100 * 100) / 100,
                  }));
                }}
                InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">%</Typography> }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label={`Comisión (${moneda})`} type="number"
                value={entrega.comision_monto}
                disabled={entrega.tipo_comision === 'PORCENTAJE'}
                onChange={(e) => {
                  const flat = e.target.value;
                  setEntrega(prev => ({
                    ...prev,
                    comision_monto: flat,
                    comision_porcentaje: (parseFloat(prev.precio) || 0) > 0 ? Math.round((parseFloat(flat) || 0) / (parseFloat(prev.precio) || 1) * 10000) / 100 : 0,
                  }));
                }}
              />
            </Grid>
          </Grid>

          {/* Detalle */}
          <TextField fullWidth size="small" label="¿Qué se entrega?" multiline rows={2}
            placeholder="Ej: 2 pollos a la brasa + papas + gaseosa"
            value={entrega.detalle_entrega} onChange={(e) => setEntrega(prev => ({ ...prev, detalle_entrega: e.target.value }))}
            sx={{ mb: 2 }} />

          {/* Dirección con Google Places */}
          <DireccionInput
            label="Dirección de entrega *"
            value={{ direccion: entrega.direccion, latitud: entrega.latitud, longitud: entrega.longitud, referencia: entrega.referencia }}
            onChange={(val) => setEntrega(prev => ({ ...prev, ...val }))}
            requiereCoordenadas={true}
            showContacto={false}
            showMap={true}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleAgregarEntrega} disabled={saving}
            sx={{ bgcolor: '#1A237E', '&:hover': { bgcolor: '#0D47A1' } }}>
            {saving ? 'Guardando...' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Asignar Motorizado */}
      <Dialog open={asignarOpen} onClose={() => !asignando && setAsignarOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TruckIcon sx={{ color: '#10B981' }} />
            <Typography variant="h6" fontWeight={700}>Asignar Motorizado</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Motorizado */}
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Motorizado *</InputLabel>
            <Select value={motorizado.id_trabajador || ''} label="Motorizado *"
              onChange={(e) => setMotorizado(prev => ({ ...prev, id_trabajador: e.target.value }))}>
              {trabajadores.map(t => (
                <MenuItem key={t.id_trabajador} value={t.id_trabajador}>
                  {t.nombre_trabajador} {t.apellido_trabajador}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Fecha y Hora del servicio */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Fecha y hora de inicio de ruta
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Fecha" type="date"
                value={motorizado.fecha_servicio}
                onChange={(e) => setMotorizado(prev => ({ ...prev, fecha_servicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Hora" type="time"
                value={motorizado.hora_servicio}
                onChange={(e) => setMotorizado(prev => ({ ...prev, hora_servicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Listado de entregas con comisión editable */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Comisión por entrega ({entregas.length} entregas)
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 2, mb: 1.5 }}>
            {comisionesEdit.map((c, idx) => (
              <Box key={c.id_orden_servicio}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                  borderBottom: idx < comisionesEdit.length - 1 ? '1px solid #F1F5F9' : 'none',
                  '&:hover': { bgcolor: '#F8FAFC' } }}>
                <Chip label={idx + 1} size="small" sx={{ bgcolor: '#1A237E', color: '#fff', fontWeight: 700, minWidth: 28 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{c.nombre || 'Sin cliente'}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {c.servicio || 'Sin servicio'} — {moneda} {c.precio.toFixed(2)}
                  </Typography>
                </Box>
                <TextField size="small" type="number" label={moneda}
                  value={c.comision_flat}
                  onChange={(e) => handleComisionChange(idx, e.target.value)}
                  sx={{ width: 100 }}
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
              </Box>
            ))}
          </Box>

          {/* Totales */}
          <Paper sx={{ p: 1.5, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Monto total entregas</Typography>
              <Typography variant="body2" fontWeight={700}>{moneda} {comisionesEdit.reduce((s, c) => s + c.precio, 0).toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Comisión total tracker</Typography>
              <Typography variant="body2" fontWeight={700} color="#10B981">{moneda} {comisionTotalEdit.toFixed(2)}</Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setAsignarOpen(false)} disabled={asignando}>Cancelar</Button>
          <Button variant="contained" onClick={handleAsignarMotorizado} disabled={asignando || !motorizado.id_trabajador}
            sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}>
            {asignando ? 'Asignando...' : `Asignar a ${entregas.length} entregas`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Resumen completo */}
      <Dialog open={resumenOpen} onClose={() => setResumenOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0', bgcolor: '#1A237E', color: '#fff' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ViewIcon />
            <Typography variant="h6" fontWeight={700}>Resumen de Ruta #{id}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Tracker asignado */}
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: trackerAsignado ? '2px solid #10B981' : '2px dashed #E2E8F0',
            bgcolor: trackerAsignado ? '#F0FDF4' : '#F8FAFC', textAlign: 'center' }}>
            {trackerAsignado ? (
              <Box>
                <TruckIcon sx={{ fontSize: 32, color: '#10B981', mb: 0.5 }} />
                <Typography variant="h5" fontWeight={800} color="#10B981">
                  {trackerAsignado.nombre_trabajador} {trackerAsignado.apellido_trabajador || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">Tracker asignado a esta ruta</Typography>
              </Box>
            ) : (
              <Box>
                <PersonIcon sx={{ fontSize: 32, color: '#94A3B8', mb: 0.5 }} />
                <Typography variant="body1" fontWeight={600} color="text.secondary">Sin tracker asignado</Typography>
              </Box>
            )}
          </Paper>

          {/* Info general */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                <Typography variant="caption" color="text.secondary">Punto de recojo</Typography>
                <Typography variant="body1" fontWeight={600}>{orden?.direccion_origen || orden?.nombre_local || '-'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                <Typography variant="caption" color="text.secondary">Fecha</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {orden?.fecha_orden ? new Date(orden.fecha_orden).toLocaleDateString() : '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                <Typography variant="caption" color="text.secondary">Hora inicio</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {orden?.fecha_orden ? new Date(orden.fecha_orden).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Totales */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #EEF2FF, #F0F9FF)', border: '1px solid #C7D2FE' }}>
            <Grid container spacing={2} textAlign="center">
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Total entregas</Typography>
                <Typography variant="h4" fontWeight={800} color="#1A237E">{resumen.total_entregas || 0}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Completadas</Typography>
                <Typography variant="h4" fontWeight={800} color="#10B981">{resumen.completadas || 0}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Monto total</Typography>
                <Typography variant="h4" fontWeight={800} color="#1A237E">{moneda} {(resumen.monto_total || 0).toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">Comisión tracker</Typography>
                <Typography variant="h4" fontWeight={800} color="#F59E0B">{moneda} {(resumen.comision_total || 0).toFixed(2)}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Detalle de entregas */}
          <Typography variant="subtitle2" fontWeight={700} color="#1A237E" sx={{ mb: 1 }}>
            Detalle de entregas
          </Typography>
          <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', bgcolor: '#F8FAFC', px: 2, py: 1, borderBottom: '1px solid #E2E8F0' }}>
              <Typography variant="caption" fontWeight={700} sx={{ width: 30 }}>#</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>Cliente</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 150 }}>Servicio</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 120 }}>Dirección</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 80, textAlign: 'right' }}>Monto</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 80, textAlign: 'right' }}>Comisión</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ width: 90, textAlign: 'center' }}>Estado</Typography>
            </Box>
            {/* Rows */}
            {entregas.map((e, idx) => {
              const color = STATUS_COLORS[e.id_estado_servicio] || '#999';
              return (
                <Box key={e.id_orden_servicio}
                  sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.2,
                    borderBottom: idx < entregas.length - 1 ? '1px solid #F1F5F9' : 'none',
                    '&:hover': { bgcolor: '#FAFBFF' } }}>
                  <Typography variant="body2" fontWeight={700} sx={{ width: 30 }} color="#1A237E">
                    {e.orden_secuencia || idx + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {e.nombre_cliente} {e.apellido_cliente}
                    </Typography>
                    {e.telefono_cliente && (
                      <Typography variant="caption" color="text.secondary">{e.telefono_cliente}</Typography>
                    )}
                  </Box>
                  <Typography variant="body2" noWrap sx={{ width: 150 }} color="text.secondary">
                    {e.nombre_servicio || '-'}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ width: 120 }} color="text.secondary">
                    {e.direccion_origen || '-'}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ width: 80, textAlign: 'right' }}>
                    {moneda} {(e.precio_total || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ width: 80, textAlign: 'right' }} color="#F59E0B">
                    {moneda} {(e.comision_entrega || 0).toFixed(2)}
                  </Typography>
                  <Box sx={{ width: 90, textAlign: 'center' }}>
                    <Chip label={e.descripcion_estado || 'Pendiente'} size="small"
                      sx={{ bgcolor: `${color}20`, color, fontWeight: 600, fontSize: 10 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Detalle de entrega (qué se entrega) */}
          {entregas.some(e => e.detalle_entrega) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#1A237E" sx={{ mb: 1 }}>
                Detalle de productos
              </Typography>
              {entregas.filter(e => e.detalle_entrega).map((e, idx) => (
                <Box key={e.id_orden_servicio} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                  <Chip label={e.orden_secuencia || idx + 1} size="small"
                    sx={{ bgcolor: '#EEF2FF', color: '#1A237E', fontWeight: 700, minWidth: 28 }} />
                  <Typography variant="body2">{e.detalle_entrega}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setResumenOpen(false)}>Cerrar</Button>
          <Button variant="outlined" startIcon={<TruckIcon />} disabled={estadoPadre >= 3}
            onClick={() => { setResumenOpen(false); handleOpenAsignar(); }} color="success">
            {trackerAsignado ? 'Cambiar tracker' : 'Asignar tracker'}
          </Button>
          <Button variant="outlined" startIcon={<SendIcon />} onClick={() => { setResumenOpen(false); handleEnviarTracking(); }}>
            Enviar tracking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Enviar Tracking */}
      <Dialog open={trackingOpen} onClose={() => !trackingSending && setTrackingOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #E2E8F0' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SendIcon sx={{ color: '#1A237E' }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Enviar tracking a clientes</Typography>
              <Typography variant="caption" color="text.secondary">
                Ruta #{id} — Se enviará email con link de seguimiento
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {trackingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : trackingPreview?.destinatarios ? (
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {trackingPreview.destinatarios.filter(d => d.email).length} de {trackingPreview.destinatarios.length} tienen email
              </Typography>
              <Box sx={{ maxHeight: 350, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: 2 }}>
                {trackingPreview.destinatarios.map((d, idx) => (
                  <Box key={d.id_orden_servicio}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2,
                      borderBottom: idx < trackingPreview.destinatarios.length - 1 ? '1px solid #F1F5F9' : 'none',
                      opacity: d.email ? 1 : 0.5 }}>
                    <Chip label={d.secuencia || idx + 1} size="small"
                      sx={{ bgcolor: '#1A237E', color: '#fff', fontWeight: 700, minWidth: 28 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{d.nombre || 'Sin nombre'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {d.email || 'Sin email'}{d.detalle ? ` — ${d.detalle}` : ''}
                      </Typography>
                    </Box>
                    {d.ultimo_envio ? (
                      <Chip label={`${d.ultimo_envio.resultado === 'OK' ? 'Enviado' : 'Error'} ${new Date(d.ultimo_envio.fecha_envio).toLocaleDateString()}`}
                        size="small"
                        sx={{ fontSize: 10, fontWeight: 600, height: 22,
                          bgcolor: d.ultimo_envio.resultado === 'OK' ? '#DCFCE7' : '#FEE2E2',
                          color: d.ultimo_envio.resultado === 'OK' ? '#16A34A' : '#DC2626' }} />
                    ) : (
                      <Chip label="Nuevo" size="small"
                        sx={{ fontSize: 10, fontWeight: 600, height: 22, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
                    )}
                  </Box>
                ))}
              </Box>
              {trackingPreview.destinatarios.some(d => d.ultimo_envio) && (
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, fontSize: 12 }}>
                  Algunos clientes ya recibieron tracking. Se reenviará a todos con email.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setTrackingOpen(false)} disabled={trackingSending}>Cancelar</Button>
          <Button variant="contained" startIcon={trackingSending ? null : <SendIcon />}
            onClick={handleConfirmarEnvio} disabled={trackingSending || trackingLoading}
            sx={{ bgcolor: '#1A237E', '&:hover': { bgcolor: '#0D47A1' } }}>
            {trackingSending ? 'Enviando...' : `Enviar a ${trackingPreview?.destinatarios?.filter(d => d.email).length || 0} clientes`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
